<template>
    <div class="viz-root">
        <v-card outlined class="pa-4 mb-4">
            <div class="d-flex flex-wrap align-center justify-space-between">
                <div>
                    <div class="secao-titulo">Consolidado do período</div>
                    <div class="caption grey--text text--darken-1">
                        Lê os totais já calculados de cada mês, sem varrer os lançamentos —
                        por isso abre rápido mesmo com vários meses.
                    </div>
                </div>

                <div class="d-flex flex-wrap mt-3 mt-md-0">
                    <v-btn
                        color="green darken-3"
                        dark
                        class="mr-2 mb-2"
                        :loading="exportando"
                        :disabled="!resumos.length"
                        title="Baixa o consolidado em Excel, com uma aba por visão"
                        @click="exportar"
                    >
                        <v-icon left>mdi-microsoft-excel</v-icon>
                        Exportar Excel
                    </v-btn>

                    <v-btn
                        color="orange darken-3"
                        outlined
                        class="mb-2"
                        :loading="recalculando"
                        title="Reconstrói os totais a partir dos lançamentos de cada mês"
                        @click="recalcular"
                    >
                        <v-icon left>mdi-database-refresh</v-icon>
                        {{ recalculando ? progresso : 'Recalcular período' }}
                    </v-btn>
                </div>
            </div>

            <v-row dense class="mt-3">
                <v-col cols="12" sm="4" md="3">
                    <v-text-field
                        v-model="mesInicial"
                        type="month"
                        label="De"
                        outlined
                        dense
                        hide-details
                        @change="carregar"
                    ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4" md="3">
                    <v-text-field
                        v-model="mesFinal"
                        type="month"
                        label="Até"
                        outlined
                        dense
                        hide-details
                        @change="carregar"
                    ></v-text-field>
                </v-col>
                <v-col cols="12" md="6" class="d-flex align-center flex-wrap">
                    <v-btn small text class="mr-2" @click="definirPeriodo(3)">3 meses</v-btn>
                    <v-btn small text class="mr-2" @click="definirPeriodo(6)">6 meses</v-btn>
                    <v-btn small text class="mr-2" @click="definirPeriodo(12)">12 meses</v-btn>
                    <v-btn small text @click="definirPeriodo(24)">24 meses</v-btn>
                </v-col>
            </v-row>
        </v-card>

        <v-progress-linear v-if="carregando" indeterminate color="teal"></v-progress-linear>

        <v-alert v-if="mesesSemResumo.length" dense text type="warning" class="caption">
            Sem totais salvos para: {{ mesesSemResumo.map(nomeDoMes).join(', ') }}.
            Use "Recalcular período" para gerá-los.
        </v-alert>

        <div v-if="resumos.length">
            <div class="kpi-grid mb-6">
                <div v-for="kpi in indicadores" :key="kpi.rotulo" class="kpi-card" :class="kpi.destaque ? 'kpi-card--destaque' : ''">
                    <div class="kpi-card__label">{{ kpi.rotulo }}</div>
                    <div class="kpi-card__value" :class="kpi.negativo ? 'kpi-card__value--negativo' : ''">{{ kpi.valor }}</div>
                    <div class="kpi-card__hint">{{ kpi.detalhe }}</div>
                </div>
            </div>

            <v-card outlined class="pa-4 mb-4">
                <div class="secao-titulo mb-1">Faturamento, despesas e lucro por mês</div>
                <div class="caption grey--text text--darken-1 mb-3">
                    Todos os valores em reais, na mesma escala. Passe o mouse em um mês para ver os números.
                </div>

                <div class="grafico-wrapper">
                    <svg
                        class="grafico"
                        :viewBox="`0 0 ${largura} ${altura}`"
                        role="img"
                        aria-label="Faturamento, despesas e lucro por mês"
                    >
                        <!-- grade recessiva -->
                        <g>
                            <line
                                v-for="linha in linhasGrade"
                                :key="'g' + linha.valor"
                                :x1="margem.esquerda" :x2="largura - margem.direita"
                                :y1="linha.y" :y2="linha.y"
                                :stroke="linha.zero ? cores.eixo : cores.grade"
                                stroke-width="1"
                            ></line>
                            <text
                                v-for="linha in linhasGrade"
                                :key="'t' + linha.valor"
                                :x="margem.esquerda - 8" :y="linha.y + 4"
                                text-anchor="end" class="rotulo-eixo"
                            >{{ linha.rotulo }}</text>
                        </g>

                        <!-- colunas -->
                        <g v-for="grupo in grupos" :key="grupo.mes" class="camada-dados">
                            <rect
                                v-if="mesEmFoco === grupo.mes"
                                :x="grupo.x" y="8"
                                :width="grupo.largura" :height="altura - margem.baixo - 8"
                                :fill="cores.realce"
                            ></rect>

                            <path
                                v-for="barra in grupo.barras"
                                :key="grupo.mes + barra.serie"
                                :d="barra.path"
                                :fill="barra.cor"
                            ></path>

                            <text
                                :x="grupo.centro" :y="altura - margem.baixo + 18"
                                text-anchor="middle" class="rotulo-eixo"
                            >{{ grupo.rotulo }}</text>
                        </g>

                        <!-- Camada de hover por cima de tudo: se ficasse embaixo das
                             barras, o destaque roubaria o ponteiro e o tooltip piscaria. -->
                        <g>
                            <rect
                                v-for="grupo in grupos"
                                :key="'hover-' + grupo.mes"
                                :x="grupo.x" y="0"
                                :width="grupo.largura" :height="altura - margem.baixo"
                                fill="transparent"
                                @mouseenter="mesEmFoco = grupo.mes"
                                @mouseleave="sairDoFoco(grupo.mes)"
                            ></rect>
                        </g>
                    </svg>

                    <div v-if="dadosEmFoco" class="tooltip">
                        <div class="tooltip__titulo">{{ dadosEmFoco.titulo }}</div>
                        <div v-for="linha in dadosEmFoco.linhas" :key="linha.rotulo" class="tooltip__linha">
                            <span class="tooltip__cor" :style="{ backgroundColor: linha.cor }"></span>
                            <span class="tooltip__rotulo">{{ linha.rotulo }}</span>
                            <span class="tooltip__valor">{{ linha.valor }}</span>
                        </div>
                    </div>
                </div>

                <div class="legenda">
                    <span v-for="serie in series" :key="serie.chave" class="legenda__item">
                        <span class="legenda__cor" :style="{ backgroundColor: serie.cor }"></span>
                        {{ serie.rotulo }}
                    </span>
                </div>
            </v-card>

            <v-card outlined class="pa-4 mb-4">
                <div class="secao-titulo mb-2">Mês a mês</div>
                <v-simple-table dense>
                    <template v-slot:default>
                        <thead>
                            <tr>
                                <th class="text-left">Mês</th>
                                <th class="text-right">Atendimentos</th>
                                <th class="text-right">Faturamento</th>
                                <th class="text-right">Comissões</th>
                                <th class="text-right">Despesas operacionais</th>
                                <th class="text-right">Lucro</th>
                                <th class="text-right">Margem</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in resumos" :key="item.mes">
                                <td class="font-weight-medium">{{ nomeDoMes(item.mes) }}</td>
                                <td class="text-right">{{ item.quantidadeRecebidos || 0 }}</td>
                                <td class="text-right">{{ formatarMoeda(item.faturamentoBruto) }}</td>
                                <td class="text-right">{{ formatarMoeda(item.comissoes) }}</td>
                                <td class="text-right">{{ formatarMoeda(item.despesasOperacionais) }}</td>
                                <td class="text-right font-weight-bold" :class="item.lucro < 0 ? 'red--text text--darken-2' : ''">
                                    {{ formatarMoeda(item.lucro) }}
                                </td>
                                <td class="text-right">{{ formatarPercentual(item.margem) }}</td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr class="linha-total">
                                <td class="font-weight-bold">Total</td>
                                <td class="text-right font-weight-bold">{{ consolidado.quantidadeRecebidos }}</td>
                                <td class="text-right font-weight-bold">{{ formatarMoeda(consolidado.faturamentoBruto) }}</td>
                                <td class="text-right font-weight-bold">{{ formatarMoeda(consolidado.comissoes) }}</td>
                                <td class="text-right font-weight-bold">{{ formatarMoeda(consolidado.despesasOperacionais) }}</td>
                                <td class="text-right font-weight-bold">{{ formatarMoeda(consolidado.lucro) }}</td>
                                <td class="text-right font-weight-bold">{{ formatarPercentual(consolidado.margem) }}</td>
                            </tr>
                        </tfoot>
                    </template>
                </v-simple-table>
            </v-card>

            <v-row>
                <v-col cols="12" md="6">
                    <v-card outlined class="pa-4">
                        <div class="secao-titulo mb-2">Por barbeiro no período</div>
                        <div v-for="item in consolidado.porBarbeiro" :key="item.nome" class="barra-item">
                            <div class="d-flex justify-space-between align-center">
                                <span class="body-2 font-weight-medium">{{ item.nome }}</span>
                                <span class="body-2">{{ formatarMoeda(item.faturamento) }}</span>
                            </div>
                            <div class="barra-trilho">
                                <div class="barra-preenchida" :style="{ width: proporcao(item.faturamento, consolidado.porBarbeiro), backgroundColor: cores.serie1 }"></div>
                            </div>
                            <div class="caption grey--text text--darken-1">
                                {{ item.quantidade }} atendimento(s) · líquido {{ formatarMoeda(item.liquido) }}
                            </div>
                        </div>
                    </v-card>
                </v-col>

                <v-col cols="12" md="6">
                    <v-card outlined class="pa-4">
                        <div class="secao-titulo mb-2">Por categoria no período</div>
                        <div v-for="item in consolidado.porCategoria" :key="item.nome" class="barra-item">
                            <div class="d-flex justify-space-between align-center">
                                <span class="body-2 font-weight-medium">{{ item.nome }}</span>
                                <span class="body-2">{{ formatarMoeda(item.faturamento) }}</span>
                            </div>
                            <div class="barra-trilho">
                                <div class="barra-preenchida" :style="{ width: proporcao(item.faturamento, consolidado.porCategoria), backgroundColor: cores.serie1 }"></div>
                            </div>
                            <div class="caption grey--text text--darken-1">
                                {{ item.quantidade }} atendimento(s)
                            </div>
                        </div>
                    </v-card>
                </v-col>
            </v-row>
        </div>

        <v-card v-else-if="!carregando" outlined class="pa-8 text-center">
            <v-icon color="grey lighten-1" size="48">mdi-chart-timeline-variant</v-icon>
            <div class="secao-titulo mt-3">Nenhum total salvo neste período</div>
            <div class="body-2 grey--text text--darken-1 mt-1">
                Abra um mês na aba Painel ou use "Recalcular período" para gerar os consolidados.
            </div>
        </v-card>
    </div>
</template>

<script>
import { listarResumosMensais, recalcularResumosDoPeriodo } from '@/services/financeiro'
import {
    consolidarPeriodo,
    mesesDoIntervalo,
    nomeDoMes,
    formatarMoeda,
    formatarPercentual,
    numero
} from '@/services/financeiroCalculos'
import {
    calcularEscala,
    montarGrupos,
    montarLinhasGrade
} from '@/services/financeiroGrafico'

// Paleta categórica validada (validate_palette.js, modo claro, superfície #ffffff):
// separação CVD ΔE 9.2 e visão normal ΔE 27.6 no pior par adjacente.
// O aqua fica em 2,82:1 de contraste, abaixo de 3:1 — por isso a tabela
// "Mês a mês" acompanha o gráfico (regra de relevo).
const CORES = {
    serie1: '#2a78d6',
    serie2: '#eb6834',
    serie3: '#1baf7a',
    grade: '#e1e0d9',
    eixo: '#c3c2b7',
    realce: 'rgba(11, 11, 11, 0.04)'
}

export default {
    props: ['barbeiros', 'usuario', 'nomeBarbearia'],

    data(){
        return {
            mesInicial: '',
            mesFinal: '',
            resumos: [],
            carregando: false,
            recalculando: false,
            exportando: false,
            progresso: '',
            mesEmFoco: '',
            cores: CORES,
            largura: 960,
            altura: 320,
            margem: { esquerda: 74, direita: 16, cima: 16, baixo: 34 },
            series: [
                { chave: 'faturamentoBruto', rotulo: 'Faturamento', cor: CORES.serie1 },
                { chave: 'despesasOperacionais', rotulo: 'Despesas operacionais', cor: CORES.serie2 },
                { chave: 'lucro', rotulo: 'Lucro', cor: CORES.serie3 }
            ]
        }
    },

    computed: {
        consolidado(){
            return consolidarPeriodo(this.resumos)
        },

        mesesSemResumo(){
            const existentes = this.resumos.map((item) => item.mes)
            return mesesDoIntervalo(this.mesInicial, this.mesFinal)
                .filter((mes) => !existentes.includes(mes))
        },

        indicadores(){
            const c = this.consolidado
            return [
                { rotulo: 'Faturamento do período', valor: this.formatarMoeda(c.faturamentoBruto), detalhe: `${c.meses} mês(es) · ${c.quantidadeRecebidos} atendimentos` },
                { rotulo: 'Comissões', valor: this.formatarMoeda(c.comissoes), detalhe: `Taxas de cartão: ${this.formatarMoeda(c.taxasCartao)}` },
                { rotulo: 'Despesas operacionais', valor: this.formatarMoeda(c.despesasOperacionais), detalhe: c.despesasEmAberto ? `+ ${this.formatarMoeda(c.despesasEmAberto)} em aberto` : 'Taxas de cartão ficam separadas' },
                { rotulo: 'Lucro do período', valor: this.formatarMoeda(c.lucro), detalhe: `Média de ${this.formatarMoeda(c.mediaLucro)} por mês`, destaque: true, negativo: c.lucro < 0 },
                { rotulo: 'Margem do período', valor: this.formatarPercentual(c.margem), detalhe: 'Lucro sobre faturamento líquido', destaque: true, negativo: c.margem < 0 },
                { rotulo: 'Melhor mês', valor: c.melhorMes ? nomeDoMes(c.melhorMes.mes) : '—', detalhe: c.melhorMes ? `Lucro de ${this.formatarMoeda(c.melhorMes.lucro)}` : 'Sem dados' }
            ]
        },

        escala(){
            const valores = []
            this.resumos.forEach((item) => {
                this.series.forEach((serie) => valores.push(numero(item[serie.chave])))
            })
            return calcularEscala(valores)
        },

        linhasGrade(){
            return montarLinhasGrade(this.escala, this.altura, this.margem).map((linha) => ({
                ...linha,
                rotulo: this.rotuloCurto(linha.valor)
            }))
        },

        grupos(){
            return montarGrupos(this.resumos, this.series, {
                largura: this.largura,
                altura: this.altura,
                margem: this.margem,
                escala: this.escala,
                rotulo: nomeDoMes
            })
        },

        dadosEmFoco(){
            if(!this.mesEmFoco){
                return null
            }

            const item = this.resumos.find((resumo) => resumo.mes === this.mesEmFoco)
            if(!item){
                return null
            }

            return {
                titulo: nomeDoMes(item.mes),
                linhas: this.series.map((serie) => ({
                    rotulo: serie.rotulo,
                    cor: serie.cor,
                    valor: this.formatarMoeda(item[serie.chave])
                }))
            }
        }
    },

    created(){
        this.definirPeriodo(12)
    },

    methods: {
        formatarMoeda,
        formatarPercentual,
        nomeDoMes,

        // Os atalhos sempre terminam no mês corrente e contam para trás,
        // independente do mês aberto nas outras abas.
        definirPeriodo(quantidadeMeses){
            const hoje = new Date()
            const fim = new Date(hoje.getFullYear(), hoje.getMonth(), 1)
            const inicio = new Date(hoje.getFullYear(), hoje.getMonth() - (quantidadeMeses - 1), 1)

            this.mesFinal = this.formatarMesISO(fim)
            this.mesInicial = this.formatarMesISO(inicio)
            this.carregar()
        },

        // Só limpa se o mês que saiu ainda for o que está em foco: evita apagar
        // o destaque quando o ponteiro já entrou no mês vizinho.
        sairDoFoco(mes){
            if(this.mesEmFoco === mes){
                this.mesEmFoco = ''
            }
        },

        formatarMesISO(data){
            return `${data.getFullYear()}-${`${data.getMonth() + 1}`.padStart(2, '0')}`
        },

        async carregar(){
            if(!this.mesInicial || !this.mesFinal || this.mesInicial > this.mesFinal){
                return
            }

            this.carregando = true
            try {
                this.resumos = await listarResumosMensais(this.mesInicial, this.mesFinal)
            } catch (error) {
                this.$toasted.global.defaultError({ msg: error.message })
            } finally {
                this.carregando = false
            }
        },

        async exportar(){
            if(!this.resumos.length){
                return
            }

            this.exportando = true
            try {
                // O ExcelJS só é carregado quando alguém exporta de fato, para
                // não pesar o bundle inicial da área administrativa.
                const modulo = await import(/* webpackChunkName: "exportacao-financeiro" */ '@/services/financeiroExportacao')
                const agora = new Date()

                const blob = await modulo.gerarArquivoConsolidado({
                    resumos: this.resumos,
                    consolidado: this.consolidado,
                    nomeBarbearia: this.nomeBarbearia || 'Barbearia Lisboa',
                    subtitulo: `Período de ${nomeDoMes(this.mesInicial)} a ${nomeDoMes(this.mesFinal)}`
                        + ` · gerado em ${agora.toLocaleString('pt-BR')}`
                        + ((this.usuario && (this.usuario.name || this.usuario.email))
                            ? ` por ${this.usuario.name || this.usuario.email}` : ''),
                    usuario: this.usuario ? (this.usuario.name || this.usuario.email) : '',
                    periodo: { mesesSemDados: this.mesesSemResumo }
                })

                modulo.baixarArquivo(blob, `consolidado-financeiro-${this.mesInicial}-a-${this.mesFinal}.xlsx`)
                this.$toasted.global.defaultSuccess('Excel gerado!')
            } catch (error) {
                this.$toasted.global.defaultError({ msg: error.message })
            } finally {
                this.exportando = false
            }
        },

        async recalcular(){
            const meses = mesesDoIntervalo(this.mesInicial, this.mesFinal)
            if(!meses.length){
                return
            }

            this.recalculando = true
            this.progresso = `0/${meses.length}`
            try {
                const resultado = await recalcularResumosDoPeriodo(
                    meses,
                    this.barbeiros,
                    this.usuario,
                    (feitos, total) => { this.progresso = `${feitos}/${total}` }
                )

                this.$toasted.global.defaultSuccess(
                    `${resultado.meses} mês(es) processado(s), ${resultado.atualizados} atualizado(s).`
                )
                await this.carregar()
            } catch (error) {
                this.$toasted.global.defaultError({ msg: error.message })
            } finally {
                this.recalculando = false
                this.progresso = ''
            }
        },

        rotuloCurto(valor){
            const numeroValor = numero(valor)
            if(Math.abs(numeroValor) >= 1000){
                return `${(numeroValor / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}k`
            }
            return numeroValor.toLocaleString('pt-BR', { maximumFractionDigits: 0 })
        },

        proporcao(valor, lista){
            const maior = Math.max(...lista.map((item) => numero(item.faturamento)), 0)
            return maior ? `${Math.max((numero(valor) / maior) * 100, 2)}%` : '2%'
        }
    }
}
</script>

<style scoped>
.secao-titulo {
    font-size: 18px;
    font-weight: 800;
    color: #14312d;
}

.kpi-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
}

.kpi-card {
    padding: 16px 18px;
    border-radius: 16px;
    background: linear-gradient(180deg, #f7fbfa 0%, #eef5f4 100%);
    border: 1px solid #d5e5e2;
}

.kpi-card--destaque {
    background: linear-gradient(180deg, #e8f5e9 0%, #dcefdd 100%);
    border-color: #a5d6a7;
}

.kpi-card__label {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #5f7a76;
    margin-bottom: 6px;
}

.kpi-card__value {
    font-size: 24px;
    line-height: 1.1;
    font-weight: 800;
    color: #14312d;
    overflow-wrap: anywhere;
}

.kpi-card__value--negativo {
    color: #c62828;
}

.kpi-card__hint {
    margin-top: 6px;
    font-size: 12px;
    color: #6b8480;
}

.grafico-wrapper {
    position: relative;
    overflow-x: auto;
}

.grafico {
    width: 100%;
    min-width: 620px;
    height: auto;
    display: block;
}

.rotulo-eixo {
    font-size: 11px;
    fill: #898781;
    font-variant-numeric: tabular-nums;
}

/* Marcas e rótulos não recebem ponteiro: só a camada de hover recebe,
   senão o alvo muda embaixo do mouse e o tooltip pisca. */
.camada-dados {
    pointer-events: none;
}

.tooltip {
    position: absolute;
    top: 8px;
    right: 8px;
    background: #ffffff;
    border: 1px solid rgba(11, 11, 11, 0.1);
    border-radius: 10px;
    padding: 10px 12px;
    box-shadow: 0 4px 14px rgba(11, 11, 11, 0.08);
    pointer-events: none;
    min-width: 190px;
}

.tooltip__titulo {
    font-size: 13px;
    font-weight: 700;
    color: #0b0b0b;
    margin-bottom: 6px;
}

.tooltip__linha {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #52514e;
}

.tooltip__cor {
    width: 10px;
    height: 10px;
    border-radius: 3px;
    flex: 0 0 10px;
}

.tooltip__rotulo {
    flex: 1;
}

.tooltip__valor {
    font-weight: 700;
    color: #0b0b0b;
    font-variant-numeric: tabular-nums;
}

.legenda {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-top: 10px;
    padding-left: 4px;
}

.legenda__item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #52514e;
}

.legenda__cor {
    width: 10px;
    height: 10px;
    border-radius: 3px;
}

.linha-total td {
    border-top: 2px solid #c3c2b7;
}

.barra-item + .barra-item {
    margin-top: 12px;
}

.barra-trilho {
    height: 8px;
    border-radius: 999px;
    background: #eceff1;
    overflow: hidden;
    margin: 4px 0 2px;
}

.barra-preenchida {
    height: 100%;
    border-radius: 999px;
}

@media (max-width: 960px) {
    .kpi-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 600px) {
    .kpi-grid {
        grid-template-columns: 1fr;
    }

    .tooltip {
        position: static;
        margin-top: 10px;
    }
}
</style>
